<template>
  <div class="address-map-wrapper">
    <div v-if="loading" class="address-map-status">
      <div class="spinner-border spinner-border-sm" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
      <span>Localizando endereço no mapa...</span>
    </div>
    <div v-else-if="notFound" class="address-map-status address-map-status--warning">
      <i class="bi bi-geo-alt"></i>
      <span>Não foi possível localizar esse endereço no mapa.</span>
    </div>
    <div v-show="!loading && !notFound" ref="mapEl" class="address-map"></div>
    <a
      v-if="!loading && !notFound && mapsUrl"
      :href="mapsUrl"
      target="_blank"
      rel="noopener"
      class="address-map-open-btn"
    >
      <i class="bi bi-signpost-2"></i> Abrir no mapa
    </a>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// O bundler quebra o caminho padrão dos ícones do Leaflet — corrige apontando
// para os assets já resolvidos pelo webpack.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export default {
  name: 'AddressMap',
  props: {
    address: { type: String, default: '' },
    number: { type: [String, Number], default: '' },
    neighbor: { type: String, default: '' },
    city: { type: String, default: '' },
    state: { type: String, default: '' },
    zipcode: { type: String, default: '' },
    // Se vierem preenchidas (ex: extraídas de um link do Google Maps),
    // usa direto e pula a geocodificação por texto — fica mais preciso e rápido.
    latitude: { type: [String, Number], default: null },
    longitude: { type: [String, Number], default: null },
  },
  setup(props) {
    const mapEl = ref(null);
    const loading = ref(false);
    const notFound = ref(false);
    const resolvedLat = ref(null);
    const resolvedLon = ref(null);
    let map = null;
    let marker = null;
    let lastQuery = '';

    // Link universal do Google Maps: no celular, o próprio SO costuma oferecer
    // pra abrir no app de mapas instalado (Google Maps, Waze, etc.); no
    // desktop, abre a versão web — funciona em qualquer dispositivo.
    const mapsUrl = computed(() => {
      if (resolvedLat.value == null || resolvedLon.value == null) return null;
      return `https://www.google.com/maps/search/?api=1&query=${resolvedLat.value},${resolvedLon.value}`;
    });

    function buildQuery() {
      return [props.address, props.number, props.neighbor, props.city, props.state, props.zipcode, 'Brasil']
        .filter(Boolean)
        .join(', ');
    }

    function renderMap(lat, lon) {
      resolvedLat.value = lat;
      resolvedLon.value = lon;

      if (!mapEl.value) return;

      if (!map) {
        map = L.map(mapEl.value).setView([lat, lon], 16);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
        }).addTo(map);
        marker = L.marker([lat, lon]).addTo(map);
      } else {
        map.setView([lat, lon], 16);
        marker.setLatLng([lat, lon]);
      }

      // O container fica escondido (v-show) enquanto carrega; recalcula o
      // tamanho do mapa depois que ele aparece, senão o Leaflet renderiza torto.
      setTimeout(() => map && map.invalidateSize(), 100);
    }

    async function geocodeAndRender() {
      // Já temos coordenadas salvas (ex: extraídas de um link do Google Maps) —
      // usa direto, sem gastar chamada de geocodificação por texto.
      if (props.latitude != null && props.longitude != null && props.latitude !== '' && props.longitude !== '') {
        const key = `${props.latitude},${props.longitude}`;
        if (key === lastQuery) return;
        lastQuery = key;
        loading.value = false;
        notFound.value = false;
        await nextTick();
        renderMap(parseFloat(props.latitude), parseFloat(props.longitude));
        return;
      }

      const query = buildQuery();

      // Só sobrou "Brasil" -> nenhum campo de endereço preenchido
      if (query === 'Brasil') {
        notFound.value = true;
        loading.value = false;
        return;
      }
      if (query === lastQuery) return;
      lastQuery = query;

      loading.value = true;
      notFound.value = false;

      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`;
        const response = await fetch(url, { headers: { 'Accept-Language': 'pt-BR' } });
        const results = await response.json();

        if (!results || results.length === 0) {
          notFound.value = true;
          return;
        }

        const { lat, lon } = results[0];
        loading.value = false;
        await nextTick();
        renderMap(parseFloat(lat), parseFloat(lon));
      } catch (error) {
        console.error('Erro ao localizar endereço no mapa:', error);
        notFound.value = true;
      } finally {
        loading.value = false;
      }
    }

    onMounted(geocodeAndRender);

    watch(
      () => [props.address, props.number, props.neighbor, props.city, props.state, props.zipcode, props.latitude, props.longitude],
      geocodeAndRender
    );

    onBeforeUnmount(() => {
      if (map) {
        map.remove();
        map = null;
      }
    });

    return { mapEl, loading, notFound, mapsUrl };
  }
}
</script>

<style scoped>
.address-map {
  width: 100%;
  height: 220px;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 8px;
}

.address-map-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 8px;
}

.address-map-status--warning {
  color: #9a6700;
}

.address-map-open-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  transition: border-color 0.15s, background 0.15s;
}
.address-map-open-btn:hover {
  border-color: #1a73e8;
  color: #1a73e8;
  background: #f0f7ff;
}
</style>
